import React, { ChangeEvent, Component, useEffect, useState } from "react";
import "./TableColumnSelect.scss";
import * as _ from "lodash-es";
import { FormattedMessage } from "react-intl";
import IColumns from "../../../models/IColumns";
import OwButton from "../OwButton/OwButton";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import SortOrderEnum from "../../../models/SortOrderEnum";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import OwLabel from "../OwLabel/OwLabel";
import OwToggle from "../OwToggle/OwToggle";

/**
 * Pass in columns that consist of this object pattern:
 *
 * @param columns
 * {isChecked: boolean, label: string, name: string}
 *
 * Where name is the column name.
 *
 * @param colToggledFn
 *
 * The function that gets called after a column is toggled, for redraw.
 *
 */
type Props = {
    columns: IColumns;
    colToggledFn: any;
    className?: string;
    saveAsDefaultFn?: (columns: IColumns, sortCol?: string, sortOrder?: SortOrderEnum, fixedHeader?: boolean) => void;
    sortCol?: string;
    sortOrder?: SortOrderEnum;
    availSortCol?: { title: string; value: string }[];
    showFixedHeader?: boolean;
    fixedHeader?: boolean;
};

export const TableColumnSelect = (props: Props) => {
    const [columns, setColumns] = useState({});
    const [sortCol, setSortCol] = useState(props.sortCol);
    const [sortOrder, setSortOrder] = useState(props.sortOrder);
    const [showPanel, setShowPanel] = useState(false);
    const [fixedHeader, setFixedHeader] = useState(props.fixedHeader);
    const [isSet, setIsSet] = useState(false);

    useEffect(() => {
        if (!isSet) {
            setColumns(props.columns);
            setIsSet(true);
        }
    }, [props.columns]);

    useEffect(() => {
        if (isSet && columns) {
            props.colToggledFn(columns);
        }
    }, [columns]);

    const togglePanel = () => {
        setShowPanel(!showPanel);
    };

    const toggleCol = (e: any, colName: string, index: number) => {
        const _columns = { ...columns };

        // @ts-expect-error
        _columns[`${colName}`].isChecked = !_columns[`${colName}`].isChecked;

        setColumns(_columns);
    };

    const getColumnRows = () => {
        const availColumns = Object.keys(columns);

        return availColumns.map((colName, index) => {
            // @ts-expect-error
            const col = columns[`${colName}`];

            if (col.notVisible) {
                return;
            }

            return (
                <div key={index}>
                    <OwCheckbox
                        labelClassName="no-wrap"
                        labelOnLeft={true}
                        name={`${col.name}`}
                        label={`${col.label}`}
                        checked={col.isChecked}
                        fn={(e: any) => {
                            toggleCol(e, colName, index);
                        }}
                    />
                </div>
            );
        });
    };

    const saveAsDefaults = () => {
        if (props.saveAsDefaultFn) {
            props.saveAsDefaultFn(columns, sortCol, sortOrder, fixedHeader);
        }
    };

    const handleSortOrderChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        setSortOrder(value);
    };

    const handleSortColChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        setSortCol(value);
    };

    const handleFixedHeaderChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        setFixedHeader(value);
    };

    const colRows = getColumnRows();

    return (
        <div className="TableColumnSelect me-3">
            <OwButton
                className={"btn-primary " + props.className}
                centerIconClass={"fas fa-ellipsis-h"}
                fn={() => {
                    togglePanel();
                }}
            />

            <div className={"selectBox " + (showPanel ? "" : "d-none")}>
                <div className={props.saveAsDefaultFn ? "d-flex justify-content-between align-items-center" : ""}>
                    {props.saveAsDefaultFn ? (
                        <OwButton title={"save"} className={"btn btn-sm btn-primary mb-1"} fn={saveAsDefaults} />
                    ) : null}{" "}
                    <FormattedMessage id={"columns"} defaultMessage={"Columns"} description="Columns Title" />
                </div>
                {props.showFixedHeader && (
                    <div className="mt-2 mb-2">
                        <OwToggle name="fixedHeader" label="fixed_header" active={!!props.fixedHeader} fn={handleFixedHeaderChange} />
                    </div>
                )}
                <hr className={props.sortCol ? "mb-2" : ""} />
                {props.sortCol ? <OwLabel title={"sort"} /> : null}
                {props.sortCol ? (
                    <OwSelectOption
                        fn={handleSortColChange}
                        titleProp={"title"}
                        valueProp={"value"}
                        name={"sortCol"}
                        options={props.availSortCol || []}
                        translateTitle={true}
                        selectedOption={props.sortCol}
                        hasSelectedOptionProp={false}
                    />
                ) : null}
                {props.sortOrder ? (
                    <OwSelectOption
                        fn={handleSortOrderChange}
                        titleProp={"title"}
                        valueProp={"value"}
                        name={"sortOrder"}
                        options={[
                            { title: "ascending", value: SortOrderEnum.ASC },
                            { title: "descending", value: SortOrderEnum.DESC },
                        ]}
                        translateTitle={true}
                        selectedOption={props.sortOrder}
                        hasSelectedOptionProp={false}
                    />
                ) : null}
                {props.sortCol ? <hr className={props.sortCol ? "mt-2 mb-2" : ""} /> : null}
                {colRows}
            </div>
        </div>
    );
};
